/**
 * Admin i18n — dependency-free, same contract as the portal: dotted keys,
 * `{{var}}` interpolation, `en` fallback. Strings live in the JSON dicts here.
 * rw/fr start machine-quality and are reviewed in the App Text tool.
 *
 * This is UI CHROME only (labels, buttons, headers). Lesson/question CONTENT is
 * translated separately in the Content editor and gated in D1.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import en from "./en.json";
import rw from "./rw.json";
import fr from "./fr.json";

export type AdminLocale = "en" | "rw" | "fr";
type Tree = { [k: string]: string | Tree };

const DICTS: Record<AdminLocale, Tree> = { en: en as Tree, rw: rw as Tree, fr: fr as Tree };
export const ADMIN_LANGUAGE_NAMES: Record<AdminLocale, string> = { en: "English", rw: "Kinyarwanda", fr: "Français" };

function lookup(tree: Tree | undefined, key: string): string | undefined {
  let node: string | Tree | undefined = tree;
  for (const part of key.split(".")) {
    if (node == null || typeof node !== "object") return undefined;
    node = node[part];
  }
  return typeof node === "string" ? node : undefined;
}

export function translate(locale: AdminLocale, key: string, vars?: Record<string, string | number>): string {
  const s = lookup(DICTS[locale], key) ?? lookup(DICTS.en, key) ?? key;
  if (!vars) return s;
  return s.replace(/\{\{(\w+)\}\}/g, (_, k: string) => String(vars[k] ?? `{{${k}}}`));
}

interface I18nCtx {
  locale: AdminLocale;
  setLocale: (l: AdminLocale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}
const Ctx = createContext<I18nCtx | null>(null);

export function AdminI18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<AdminLocale>("en");
  useEffect(() => {
    const l = localStorage.getItem("radar.lang") as AdminLocale | null;
    if (l === "en" || l === "rw" || l === "fr") setLocaleState(l);
  }, []);
  const setLocale = useCallback((l: AdminLocale) => {
    setLocaleState(l);
    try {
      localStorage.setItem("radar.lang", l);
    } catch {
      /* private mode */
    }
    document.documentElement.lang = l;
  }, []);
  const t = useMemo(() => (key: string, vars?: Record<string, string | number>) => translate(locale, key, vars), [locale]);
  const value = useMemo<I18nCtx>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18nCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useI18n must be used within AdminI18nProvider");
  return v;
}
export const useT = () => useI18n().t;
export const useAdminLocale = () => useI18n().locale;

/** Translated app name for the dock/store/window title, falling back to the
    registry's English name for any app not yet keyed. */
export function useAppLabel(): (id: string, fallback: string) => string {
  const { t } = useI18n();
  return (id, fallback) => {
    const k = `apps.${id}`;
    const s = t(k);
    return s === k ? fallback : s;
  };
}
